import React, { useState } from "react";
import { supabase } from "../lib/supabase";
import { Navigation } from "./Navigation";
import {
  Bot,
  Calendar,
  CheckCircle,
  Loader,
  AlertCircle,
  Building,
  Mail,
  User,
  MessageSquare,
} from "lucide-react";
// Removed EmailJS import - now using Netlify Forms
import SEO from "./SEO";
import Footer from "./Footer";

interface FormData {
  courseOption: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  questions: string;
}

const COURSE_OPTIONS = [
  "June 17, 2025 FREE Intro to AI (ZERO of 15 seats remaining): from 9:00 am to 12:00pm",
  "June 18, 2025 FREE Intro to AI (In-Person location TBD): from 1:00 pm, to 4:00pm",
  "June 19, 2025 FREE Intro to AI (Virtual): From 9:00 am to 12:00pm",
  "Other: I would like to sign up, but these times do not work for me",
];

export default function CourseRegistration() {
  const [formData, setFormData] = useState<FormData>({
    courseOption: "",
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    questions: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.courseOption) {
      setError("Please select a course option");
      return false;
    }
    if (!formData.firstName.trim()) {
      setError("Please enter your first name");
      return false;
    }
    if (!formData.lastName.trim()) {
      setError("Please enter your last name");
      return false;
    }
    if (!formData.email.trim()) {
      setError("Please enter your email address");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Save to Supabase
      const { error: dbError } = await supabase
        .from("course_registrations")
        .insert([
          {
            course_option: formData.courseOption,
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            company: formData.company || null,
            questions: formData.questions || null,
          },
        ]);

      if (dbError) throw new Error(dbError.message);

      // Send notification via Netlify Forms
      try {
        // Submit to Netlify Forms
        const formDataToSubmit = new FormData();
        formDataToSubmit.append("form-name", "course-registration");
        formDataToSubmit.append(
          "name",
          `${formData.firstName} ${formData.lastName}`,
        );
        formDataToSubmit.append("email", formData.email);
        formDataToSubmit.append("company", formData.company || "Not provided");
        formDataToSubmit.append("courseOption", formData.courseOption);
        formDataToSubmit.append("questions", formData.questions || "None");
        formDataToSubmit.append("subject", "New Free AI Course Registration");
        formDataToSubmit.append(
          "message",
          `
New Free AI Course Registration:

Course Option: ${formData.courseOption}
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Company: ${formData.company || "Not provided"}

Questions/Comments:
${formData.questions || "None"}
        `.trim(),
        );

        const response = await fetch("/", {
          method: "POST",
          body: formDataToSubmit,
        });

        if (response.ok) {
          console.log(
            "Course registration notification sent via Netlify Forms",
          );
        } else {
          console.warn(
            "Failed to send course registration notification via Netlify Forms",
          );
        }
      } catch (error) {
        console.error("Error sending course registration notification:", error);
        // Continue even if notification fails
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while submitting the form",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="One80Labs - Course Registration"
        description="Register for our AI training courses. Learn practical skills with hands-on workshops designed for professionals at all levels."
        keywords={[
          "course registration",
          "AI training",
          "workshop registration",
          "professional development",
          "technology education",
        ]}
      />
      <SEO
        title="Free Intro to AI Course Registration | One80Labs"
        description="Register for our free introductory AI course. Learn the fundamentals of AI and how it can transform your business operations."
        keywords={[
          "AI course registration",
          "free AI training",
          "intro to AI",
          "AI workshop",
          "AI for business",
          "One80Labs training",
        ]}
      />

      <Navigation onSearchClick={() => setShowSearch(!showSearch)} />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {submitted ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
            <CheckCircle className="h-16 w-16 text-[#F98B3D] mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Thank You for Registering!
            </h1>
            <div className="mb-8 text-gray-600">
              <p className="mb-4">
                Your registration for the Free Intro to AI course has been
                confirmed.
              </p>
              <p className="mb-4">
                One80Labs will be sending more information about the course in
                the following days.
              </p>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6 text-left">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Registration Details
                </h2>
                <div className="space-y-2">
                  <p>
                    <strong>Course:</strong> {formData.courseOption}
                  </p>
                  <p>
                    <strong>Name:</strong> {formData.firstName}{" "}
                    {formData.lastName}
                  </p>
                  <p>
                    <strong>Email:</strong> {formData.email}
                  </p>
                  {formData.company && (
                    <p>
                      <strong>Company:</strong> {formData.company}
                    </p>
                  )}
                  {formData.questions && (
                    <>
                      <p>
                        <strong>Questions/Comments:</strong>
                      </p>
                      <p className="whitespace-pre-wrap">
                        {formData.questions}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <p>We look forward to seeing you at the course!</p>
            </div>
            <a
              href="/"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200"
            >
              Return to Home
            </a>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              Register for Free Intro to AI Course
            </h1>
            <p className="text-gray-600 mb-8">
              Join us for a free introductory course on AI and discover how it
              can transform your business operations. Please fill out the form
              below to secure your spot.
            </p>

            {error && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-lg border border-gray-200 shadow-sm p-6"
            >
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="courseOption"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Course Selection <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="courseOption"
                      name="courseOption"
                      value={formData.courseOption}
                      onChange={handleChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                      required
                    >
                      <option value="">Select a course option</option>
                      {COURSE_OPTIONS.map((option) => {
                        const isDisabled = option.includes(
                          "ZERO of 15 seats remaining",
                        );
                        return (
                          <option
                            key={option}
                            value={option}
                            disabled={isDisabled}
                          >
                            {option}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Company (Optional)
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="questions"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Any particular questions you would like to address in the
                    class?
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                      <MessageSquare className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      id="questions"
                      name="questions"
                      value={formData.questions}
                      onChange={handleChange}
                      rows={4}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                    />
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader className="animate-spin h-5 w-5 mr-2" />
                        Submitting...
                      </>
                    ) : (
                      "Register Now"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
